import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Audit,
  TeamMemberRole,
  ChecklistResponseStatus,
  FindingSeverity,
} from '../types';
import { auditService } from '../auditService';

interface AuditDetailModalProps {
  audit: Audit | null;
  onClose: () => void;
  onRefresh: () => void;
}

export const AuditDetailModal: React.FC<AuditDetailModalProps> = ({
  audit,
  onClose,
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'TEAM' | 'CHECKLIST' | 'FINDINGS' | 'REPORT'>('OVERVIEW');
  const [auditDetail, setAuditDetail] = useState<Audit | null>(audit);
  const [loading, setLoading] = useState(false);

  // Meeting Form States
  const [openingMeetingTime, setOpeningMeetingTime] = useState(new Date().toISOString().slice(0, 16));
  const [closingMeetingTime, setClosingMeetingTime] = useState(new Date().toISOString().slice(0, 16));

  // Team Form State
  const [newUserId, setNewUserId] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newRole, setNewRole] = useState<TeamMemberRole>('AUDITOR');
  const [newDepartment, setNewDepartment] = useState('Quality Assurance');

  // Finding Form State
  const [showAddFinding, setShowAddFinding] = useState(false);
  const [findingSeverity, setFindingSeverity] = useState<FindingSeverity>('LEVEL_2_MAJOR');
  const [findingTitle, setFindingTitle] = useState('');
  const [findingClause, setFindingClause] = useState('');
  const [findingDesc, setFindingDesc] = useState('');
  const [findingTargetDate, setFindingTargetDate] = useState('2026-05-15');

  // Report Form State
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [qualityManagerId, setQualityManagerId] = useState('QUALITY_MANAGER_FATMA');

  useEffect(() => {
    if (audit?.id) {
      loadDetail(audit.id);
    }
  }, [audit?.id]);

  const loadDetail = async (id: string) => {
    try {
      setLoading(true);
      const data = await auditService.getAuditById(id);
      setAuditDetail(data);
      if (data.report?.executiveSummary) {
        setExecutiveSummary(data.report.executiveSummary);
      }
    } catch (err) {
      console.error('Error fetching audit details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordOpening = async () => {
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.recordOpeningMeeting(auditDetail.id, openingMeetingTime);
      await loadDetail(auditDetail.id);
      onRefresh();
    } catch (err) {
      alert('Failed to record opening meeting: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordClosing = async () => {
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.recordClosingMeeting(auditDetail.id, closingMeetingTime);
      await loadDetail(auditDetail.id);
      onRefresh();
    } catch (err) {
      alert('Failed to record closing meeting: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAudit = async () => {
    if (!auditDetail) return;
    if (!window.confirm('Are you sure you want to officially close and lock this audit? This action cannot be undone.')) return;
    try {
      setLoading(true);
      await auditService.closeAudit(auditDetail.id);
      await loadDetail(auditDetail.id);
      onRefresh();
      alert('Audit officially closed and locked.');
    } catch (err) {
      alert('Failed to close audit: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeamMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.addTeamMember(auditDetail.id, {
        userId: newUserId,
        userName: newUserName,
        role: newRole,
        department: newDepartment,
      });
      await loadDetail(auditDetail.id);
      setNewUserId('');
      setNewUserName('');
      onRefresh();
      alert('Team member added successfully (Independence verified).');
    } catch (err) {
      alert('Failed to add team member (SoD check failed): ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordResponse = async (
    checklistInstanceId: string,
    questionId: string,
    responseStatus: ChecklistResponseStatus
  ) => {
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.recordChecklistResponse(auditDetail.id, checklistInstanceId, {
        questionId,
        responseStatus,
        auditorNotes: 'Recorded during field execution',
      });
      await loadDetail(auditDetail.id);
      onRefresh();
    } catch (err) {
      alert('Failed to record response: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFinding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.createFinding(auditDetail.id, {
        severity: findingSeverity,
        title: findingTitle,
        description: findingDesc,
        clauseReference: findingClause,
        targetClosureDate: findingTargetDate,
      });
      await loadDetail(auditDetail.id);
      setShowAddFinding(false);
      setFindingTitle('');
      setFindingDesc('');
      setFindingClause('');
      onRefresh();
      alert('Audit finding recorded successfully.');
    } catch (err) {
      alert('Failed to log finding: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const getLeadAuditorDisplayName = (audit?: Audit | null) => {
    if (!audit) return 'Selin Arslan';
    if (audit.leadAuditorName) return audit.leadAuditorName;
    const id = audit.leadAuditorId || '';
    if (id === '11111111-0000-0000-0000-000000000007' || id.includes('audit_mgr')) return 'Selin Arslan';
    if (id === '11111111-0000-0000-0000-000000000004' || id.includes('quality_mgr')) return 'Mehmet Demir';
    if (id === '11111111-0000-0000-0000-000000000005' || id.includes('compliance_mgr')) return 'Canan Ak';
    if (id === '11111111-0000-0000-0000-000000000003' || id.includes('safety_mgr')) return 'Murat Kaya';
    if (id === '11111111-0000-0000-0000-000000000013' || id.includes('investigation_mgr')) return 'Erdal Aydeniz';
    if (id.includes('-') && id.length > 20) return 'Selin Arslan';
    return id || 'Selin Arslan';
  };

  const handleSaveReport = async () => {
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.saveReportDraft(auditDetail.id, {
        executiveSummary,
        leadAuditorSign: `Digitally Signed by ${getLeadAuditorDisplayName(auditDetail)}`,
      });
      await loadDetail(auditDetail.id);
      onRefresh();
      alert('Report draft saved and signed by Lead Auditor.');
    } catch (err) {
      alert('Failed to save report: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReport = async () => {
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.approveReport(auditDetail.id, qualityManagerId);
      await loadDetail(auditDetail.id);
      onRefresh();
      alert('Audit report approved by Quality Manager.');
    } catch (err) {
      alert('Failed to approve report: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishReport = async () => {
    if (!auditDetail) return;
    try {
      setLoading(true);
      await auditService.publishReport(auditDetail.id, qualityManagerId);
      await loadDetail(auditDetail.id);
      onRefresh();
      alert('Audit report officially published (WORM Archive).');
    } catch (err) {
      alert('Failed to publish report: ' + err);
    } finally {
      setLoading(false);
    }
  };

  if (!auditDetail) return null;
  const isClosed = auditDetail.status === 'CLOSED';

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span className="badge-pill badge-info" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {auditDetail.auditNumber}
              </span>
              <span className="badge-pill badge-neutral">
                {auditDetail.auditType}
              </span>
              <span className={`badge-pill ${isClosed ? 'badge-neutral' : 'badge-success'}`}>
                {auditDetail.status}
              </span>
            </div>
            <h3 style={{ fontSize: '19px', fontWeight: 700, margin: '0 0 4px 0', color: '#0f172a' }}>{auditDetail.title}</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
              Target: <strong style={{ color: '#334155' }}>{auditDetail.department}</strong> ({auditDetail.location}) | Lead Auditor:{' '}
              <strong style={{ color: '#334155' }}>{getLeadAuditorDisplayName(auditDetail)}</strong> ({auditDetail.leadAuditorDepartment})
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Closed Read-only Alert */}
        {isClosed && (
          <div style={{ backgroundColor: '#fffbeb', borderBottom: '1px solid #fef3c7', padding: '10px 20px', fontSize: '13px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔒</span>
            <span>
              This audit is <strong>CLOSED</strong>. Per aviation quality standards (EASA / ISO 9001), all records are locked read-only.
            </span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="nav-tabs" style={{ margin: '0 20px', borderBottom: '1px solid #e2e8f0' }}>
          <button
            onClick={() => setActiveTab('OVERVIEW')}
            className={`tab-btn ${activeTab === 'OVERVIEW' ? 'active' : ''}`}
          >
            Overview & Meetings
          </button>
          <button
            onClick={() => setActiveTab('TEAM')}
            className={`tab-btn ${activeTab === 'TEAM' ? 'active' : ''}`}
          >
            Audit Team & Independence ({auditDetail.teamMembers?.length ?? 0})
          </button>
          <button
            onClick={() => setActiveTab('CHECKLIST')}
            className={`tab-btn ${activeTab === 'CHECKLIST' ? 'active' : ''}`}
          >
            Checklists & Fieldwork ({auditDetail.checklistInstances?.length ?? 0})
          </button>
          <button
            onClick={() => setActiveTab('FINDINGS')}
            className={`tab-btn ${activeTab === 'FINDINGS' ? 'active' : ''}`}
          >
            Findings ({auditDetail.findings?.length ?? 0})
          </button>
          <button
            onClick={() => setActiveTab('REPORT')}
            className={`tab-btn ${activeTab === 'REPORT' ? 'active' : ''}`}
          >
            Reporting & Dual Signoff
          </button>
        </div>

        {/* Content Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'OVERVIEW' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Audit Scope & Criteria
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', lineHeight: 1.6 }}>{auditDetail.scopeAndCriteria}</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>Opening Meeting</span>
                    {auditDetail.openingMeetingAt ? (
                      <span className="badge-pill badge-success">Conducted</span>
                    ) : (
                      <span className="badge-pill badge-neutral">Pending</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {auditDetail.openingMeetingAt ? (
                      <span>Date: {auditDetail.openingMeetingAt}</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          type="datetime-local"
                          disabled={isClosed}
                          value={openingMeetingTime}
                          onChange={(e) => setOpeningMeetingTime(e.target.value)}
                          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                        />
                        <button
                          onClick={handleRecordOpening}
                          disabled={isClosed || loading}
                          className="btn-primary"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Confirm Opening Meeting & Start Audit
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>Closing Meeting</span>
                    {auditDetail.closingMeetingAt ? (
                      <span className="badge-pill badge-success">Conducted</span>
                    ) : (
                      <span className="badge-pill badge-neutral">Pending</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {auditDetail.closingMeetingAt ? (
                      <span>Date: {auditDetail.closingMeetingAt}</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          type="datetime-local"
                          disabled={isClosed}
                          value={closingMeetingTime}
                          onChange={(e) => setClosingMeetingTime(e.target.value)}
                          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                        />
                        <button
                          onClick={handleRecordClosing}
                          disabled={isClosed || loading}
                          className="btn-primary"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Confirm Closing Meeting & Complete Fieldwork
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {!isClosed && auditDetail.status === 'APPROVED' && (
                <div style={{ padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h5 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#991b1b' }}>Formal Audit Closure</h5>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#b91c1c' }}>Report approved. Closing the audit will lock all records permanently.</p>
                  </div>
                  <button
                    onClick={handleCloseAudit}
                    className="btn-primary"
                    style={{ backgroundColor: '#dc2626', fontSize: '12px', padding: '8px 16px' }}
                  >
                    🔒 Close Audit
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: TEAM */}
          {activeTab === 'TEAM' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name / User ID</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Independence Verification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditDetail.teamMembers?.map((m) => (
                      <tr key={m.id}>
                        <td style={{ fontWeight: 600, color: '#0f172a' }}>{m.userName} ({m.userId})</td>
                        <td style={{ fontWeight: 600, color: '#2563eb' }}>{m.role}</td>
                        <td style={{ color: '#475569' }}>{m.department}</td>
                        <td>
                          <span className="badge-pill badge-success">
                            ✓ Verified Independent (SoD OK)
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!isClosed && (
                <form onSubmit={handleAddTeamMember} style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    + Add Audit Team Member (Independence Checked)
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                    <input
                      type="text"
                      required
                      placeholder="User ID (e.g. AUD-03)"
                      value={newUserId}
                      onChange={(e) => setNewUserId(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    />
                    <input
                      type="text"
                      required
                      placeholder="Name (e.g. Jane Doe)"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    />
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value as TeamMemberRole)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                    >
                      <option value="AUDITOR">AUDITOR</option>
                      <option value="TECHNICAL_EXPERT">TECHNICAL_EXPERT</option>
                      <option value="OBSERVER">OBSERVER</option>
                    </select>
                    <input
                      type="text"
                      required
                      placeholder="Department"
                      value={newDepartment}
                      onChange={(e) => setNewDepartment(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                      style={{ fontSize: '12px', padding: '6px 14px' }}
                    >
                      Add to Team
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* TAB 3: CHECKLIST RUNNER */}
          {activeTab === 'CHECKLIST' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {auditDetail.checklistInstances?.map((inst) => (
                <div key={inst.id} className="card" style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{inst.title}</h4>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                        {inst.answeredQuestions} / {inst.totalQuestions} Questions Answered | Compliance Score: %{inst.scorePercentage}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span className="badge-pill badge-success">{inst.conformingCount} Conforming</span>
                      <span className="badge-pill badge-danger">{inst.nonConformingCount} Non-Conforming</span>
                    </div>
                  </div>

                  {/* Question Responses */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {inst.responses?.map((resp) => (
                      <div key={resp.id} style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', flex: 1 }}>{resp.questionText}</div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            type="button"
                            disabled={isClosed}
                            onClick={() => handleRecordResponse(inst.id, resp.questionId, 'CONFORMING')}
                            className={resp.responseStatus === 'CONFORMING' ? 'btn-primary' : 'btn-secondary'}
                            style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: resp.responseStatus === 'CONFORMING' ? '#10b981' : undefined }}
                          >
                            ✓ CONFORMING
                          </button>
                          <button
                            type="button"
                            disabled={isClosed}
                            onClick={() => handleRecordResponse(inst.id, resp.questionId, 'NON_CONFORMING')}
                            className={resp.responseStatus === 'NON_CONFORMING' ? 'btn-primary' : 'btn-secondary'}
                            style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: resp.responseStatus === 'NON_CONFORMING' ? '#ef4444' : undefined }}
                          >
                            ✕ NON-CONFORMING
                          </button>
                          <button
                            type="button"
                            disabled={isClosed}
                            onClick={() => handleRecordResponse(inst.id, resp.questionId, 'OBSERVATION')}
                            className={resp.responseStatus === 'OBSERVATION' ? 'btn-primary' : 'btn-secondary'}
                            style={{ fontSize: '11px', padding: '4px 8px', backgroundColor: resp.responseStatus === 'OBSERVATION' ? '#f59e0b' : undefined }}
                          >
                            👁 OBSERVATION
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TAB 4: FINDINGS */}
          {activeTab === 'FINDINGS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Audit Findings</h4>
                {!isClosed && (
                  <button
                    onClick={() => setShowAddFinding(true)}
                    className="btn-primary"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    + Log New Finding
                  </button>
                )}
              </div>

              {showAddFinding && (
                <form onSubmit={handleCreateFinding} style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '16px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h5 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Finding Information</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <input
                      type="text"
                      required
                      placeholder="Finding Title (e.g. Fuel computation sheet missing)"
                      value={findingTitle}
                      onChange={(e) => setFindingTitle(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                    />
                    <select
                      value={findingSeverity}
                      onChange={(e) => setFindingSeverity(e.target.value as FindingSeverity)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                    >
                      <option value="LEVEL_1_CRITICAL">LEVEL 1 (Critical Non-Conformity)</option>
                      <option value="LEVEL_2_MAJOR">LEVEL 2 (Major Non-Conformity)</option>
                      <option value="LEVEL_3_MINOR">LEVEL 3 (Minor Non-Conformity)</option>
                      <option value="OBSERVATION">OBSERVATION</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <input
                      type="text"
                      required
                      placeholder="Regulatory Clause (e.g. ORO.AOC.135)"
                      value={findingClause}
                      onChange={(e) => setFindingClause(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                    />
                    <input
                      type="date"
                      required
                      value={findingTargetDate}
                      onChange={(e) => setFindingTargetDate(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                    />
                  </div>
                  <textarea
                    required
                    rows={2}
                    placeholder="Detailed finding statement and objective evidence..."
                    value={findingDesc}
                    onChange={(e) => setFindingDesc(e.target.value)}
                    style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', backgroundColor: '#fff' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setShowAddFinding(false)}
                      className="btn-secondary"
                      style={{ fontSize: '12px' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                      style={{ fontSize: '12px', backgroundColor: '#dc2626' }}
                    >
                      Save Finding
                    </button>
                  </div>
                </form>
              )}

              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Finding Code</th>
                      <th>Severity</th>
                      <th>Title & Clause</th>
                      <th>Target Closure</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!auditDetail.findings || auditDetail.findings.length === 0) ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                          No findings recorded for this audit.
                        </td>
                      </tr>
                    ) : (
                      auditDetail.findings.map((f) => (
                        <tr key={f.id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#dc2626' }}>{f.findingCode}</td>
                          <td>
                            <span className="badge-pill badge-danger">{f.severity}</span>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{f.title}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{f.clauseReference}</div>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>{f.targetClosureDate}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="badge-pill badge-warning">{f.status}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 5: REPORT & DUAL SIGNOFF */}
          {activeTab === 'REPORT' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Executive Summary
                </h4>
                <textarea
                  rows={4}
                  disabled={isClosed || auditDetail.report?.status === 'PUBLISHED'}
                  placeholder="Overall audit conclusions, key findings, and systemic observations..."
                  value={executiveSummary}
                  onChange={(e) => setExecutiveSummary(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                />
                {!isClosed && auditDetail.report?.status !== 'PUBLISHED' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleSaveReport}
                      disabled={loading}
                      className="btn-primary"
                      style={{ fontSize: '12px', padding: '6px 14px' }}
                    >
                      Save Report Draft & Sign as Lead Auditor
                    </button>
                  </div>
                )}
              </div>

              {auditDetail.report && (
                <div className="card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: '#2563eb' }}>
                      Report No: {auditDetail.report.reportNumber} (v{auditDetail.report.currentVersion})
                    </span>
                    <span className="badge-pill badge-info">
                      {auditDetail.report.status}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block', textTransform: 'uppercase' }}>Lead Auditor Signature:</span>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '4px' }}>
                        {auditDetail.report.leadAuditorSign || 'Not Signed'}
                      </div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block', textTransform: 'uppercase' }}>Quality Manager Signoff (Dual SoD):</span>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginTop: '4px' }}>
                        {auditDetail.report.qualityManagerSign || 'Pending Signoff'}
                      </div>
                    </div>
                  </div>

                  {!isClosed && auditDetail.report.status === 'DRAFT' && (
                    <div style={{ paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>Approving QM ID:</label>
                        <input
                          type="text"
                          value={qualityManagerId}
                          onChange={(e) => setQualityManagerId(e.target.value)}
                          style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }}
                        />
                      </div>
                      <button
                        onClick={handleApproveReport}
                        disabled={loading}
                        className="btn-primary"
                        style={{ fontSize: '12px', padding: '6px 14px', backgroundColor: '#10b981' }}
                      >
                        ✓ Approve as Quality Manager
                      </button>
                    </div>
                  )}

                  {!isClosed && auditDetail.report.status === 'APPROVED' && (
                    <div style={{ paddingTop: '10px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9' }}>
                      <button
                        onClick={handlePublishReport}
                        disabled={loading}
                        className="btn-primary"
                        style={{ fontSize: '12px', padding: '6px 14px', backgroundColor: '#6366f1' }}
                      >
                        Publish Official Report (WORM Archive)
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
